import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';
import platformConfigService from '../../../services/platformConfigService';
import { useKeyboardNavigation } from '../../../utils/accessibility';

const PlatformRulesConfig = ({ data, onUpdate }) => {
  const [rules, setRules] = useState({
    minRentalDuration: '1',
    maxRentalDuration: '90',
    cancellationWindow: '24',
    cancellationFeePercentage: '15',
    damageAssessmentThreshold: '100',
    disputeResolutionTimeframe: '14',
    autoApproveBookings: false,
    requireInsurance: true,
    allowInstantBooking: true,
    gracePeriodHours: '2'
  });
  const [previewMode, setPreviewMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  // Load data from props
  useEffect(() => {
    if (data) {
      setRules({
        minRentalDuration: String(data?.minRentalDuration || '1'),
        maxRentalDuration: String(data?.maxRentalDuration || '90'),
        cancellationWindow: String(data?.cancellationWindow || '24'),
        cancellationFeePercentage: String(data?.cancellationFeePercentage || '15'),
        damageAssessmentThreshold: String(data?.damageAssessmentThreshold || '100'),
        disputeResolutionTimeframe: String(data?.disputeResolutionTimeframe || '14'),
        autoApproveBookings: data?.autoApproveBookings || false,
        requireInsurance: data?.requireInsurance !== false,
        allowInstantBooking: data?.allowInstantBooking !== false,
        gracePeriodHours: String(data?.gracePeriodHours || '2')
      });
    }
  }, [data]);

  // Real-time validation
  useEffect(() => {
    validateRules();
  }, [rules]);

  const validateRules = () => {
    const errors = {};

    if (parseInt(rules?.minRentalDuration) < 1) {
      errors.minRentalDuration = 'Minimum rental duration must be at least 1 day';
    }

    if (parseInt(rules?.maxRentalDuration) < parseInt(rules?.minRentalDuration)) {
      errors.maxRentalDuration = 'Maximum must be greater than minimum';
    }

    if (parseInt(rules?.cancellationWindow) < 0) {
      errors.cancellationWindow = 'Cancellation window cannot be negative';
    }

    if (parseFloat(rules?.cancellationFeePercentage) < 0 || parseFloat(rules?.cancellationFeePercentage) > 100) {
      errors.cancellationFeePercentage = 'Fee percentage must be between 0 and 100';
    }

    setValidationErrors(errors);
    return Object.keys(errors)?.length === 0;
  };

  const handleInputChange = (field, value) => {
    setRules({ ...rules, [field]: value });
  };

  const handleSave = async () => {
    if (!validateRules()) {
      return;
    }

    setSaving(true);
    try {
      await platformConfigService?.updatePlatformRules(rules);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Failed to save platform rules:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleRollback = async () => {
    try {
      const auditTrail = await platformConfigService?.getAuditTrail('platform_rules', 1);
      if (auditTrail?.length > 0) {
        await platformConfigService?.rollbackConfiguration('platform_rules', auditTrail?.[0]?.id);
        if (onUpdate) onUpdate();
      }
    } catch (error) {
      console.error('Failed to rollback:', error);
    }
  };

  const handlePreviewToggle = useKeyboardNavigation(
    () => setPreviewMode(!previewMode),
    () => setPreviewMode(!previewMode),
    null
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="FileText" size={20} className="text-primary" aria-hidden="true" />
            </div>
            <div>
              <h2 className="font-heading text-xl font-bold text-foreground">Platform Rules Configuration</h2>
              <p className="font-body text-sm text-muted-foreground">Set rental duration limits, policies, and operational parameters</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              iconName="RotateCcw"
              onClick={handleRollback}
              aria-label="Rollback to previous version"
            >
              Rollback
            </Button>
            <Button
              variant="outline"
              size="sm"
              iconName={previewMode ? 'Eye' : 'EyeOff'}
              onClick={() => setPreviewMode(!previewMode)}
              onKeyDown={handlePreviewToggle}
              aria-label={previewMode ? 'Hide preview' : 'Show preview'}
              aria-pressed={previewMode}
            >
              {previewMode ? 'Hide' : 'Show'} Preview
            </Button>
            <Button
              size="sm"
              iconName="Save"
              onClick={handleSave}
              loading={saving}
              disabled={Object.keys(validationErrors)?.length > 0}
              aria-label="Save platform rules"
            >
              Save Rules
            </Button>
          </div>
        </div>

        {previewMode && (
          <div className="mt-4 p-4 bg-primary/5 border border-primary/20 rounded-md" role="region" aria-label="Live preview">
            <h3 className="font-heading text-sm font-semibold text-foreground mb-2">Live Preview</h3>
            <p className="font-body text-sm text-muted-foreground">
              Rentals: {rules?.minRentalDuration}-{rules?.maxRentalDuration} days • 
              Cancel: {rules?.cancellationWindow}hrs ({rules?.cancellationFeePercentage}% fee) • 
              Dispute: {rules?.disputeResolutionTimeframe} days
            </p>
          </div>
        )}
      </div>

      {/* Rental Duration Settings */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-6">
        <div className="p-4 bg-primary/5 border border-primary/20 rounded-md mb-4">
          <h3 className="font-heading text-base font-semibold text-foreground mb-3 flex items-center space-x-2">
            <Icon name="Calendar" size={18} className="text-primary" strokeWidth={2} aria-hidden="true" />
            <span>Rental Duration Limits</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Minimum Rental Duration (days)"
              type="number"
              value={rules?.minRentalDuration}
              onChange={(e) => handleInputChange('minRentalDuration', e?.target?.value)}
              description="Shortest allowed rental period"
              error={validationErrors?.minRentalDuration}
              required
              aria-required="true"
            />
            <Input
              label="Maximum Rental Duration (days)"
              type="number"
              value={rules?.maxRentalDuration}
              onChange={(e) => handleInputChange('maxRentalDuration', e?.target?.value)}
              description="Longest allowed rental period"
              error={validationErrors?.maxRentalDuration}
              required
              aria-required="true"
            />
            <Input
              label="Grace Period (hours)"
              type="number"
              value={rules?.gracePeriodHours}
              onChange={(e) => handleInputChange('gracePeriodHours', e?.target?.value)}
              description="Late return tolerance window"
              aria-describedby="grace-period-help"
            />
          </div>
        </div>
      </div>

      {/* Cancellation Policy */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-6">
        <div className="p-4 bg-warning/5 border border-warning/20 rounded-md mb-4">
          <h3 className="font-heading text-base font-semibold text-foreground mb-3 flex items-center space-x-2">
            <Icon name="XCircle" size={18} className="text-warning" strokeWidth={2} aria-hidden="true" />
            <span>Cancellation Policies</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Cancellation Window (hours)"
              type="number"
              value={rules?.cancellationWindow}
              onChange={(e) => handleInputChange('cancellationWindow', e?.target?.value)}
              description="Hours before pickup for free cancellation"
              error={validationErrors?.cancellationWindow}
              required
              aria-required="true"
            />
            <Input
              label="Cancellation Fee (%)"
              type="number"
              value={rules?.cancellationFeePercentage}
              onChange={(e) => handleInputChange('cancellationFeePercentage', e?.target?.value)}
              description="Percentage charged for late cancellations"
              error={validationErrors?.cancellationFeePercentage}
              required
              aria-required="true"
            />
          </div>
        </div>
      </div>

      {/* Damage Assessment */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-6">
        <div className="p-4 bg-error/5 border border-error/20 rounded-md mb-4">
          <h3 className="font-heading text-base font-semibold text-foreground mb-3 flex items-center space-x-2">
            <Icon name="AlertTriangle" size={18} className="text-error" strokeWidth={2} aria-hidden="true" />
            <span>Damage Assessment Thresholds</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Minimum Damage Charge ($)"
              type="number"
              value={rules?.damageAssessmentThreshold}
              onChange={(e) => handleInputChange('damageAssessmentThreshold', e?.target?.value)}
              description="Minimum amount for damage claims"
              required
              aria-required="true"
            />
            <div className="space-y-3">
              <Checkbox
                label="Require Photo Evidence"
                description="Owners must upload photos for damage claims"
                checked={rules?.requireInsurance}
                onChange={(e) => handleInputChange('requireInsurance', e?.target?.checked)}
                aria-label="Require photo evidence for damage claims"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Dispute Resolution */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-6">
        <div className="p-4 bg-secondary/5 border border-secondary/20 rounded-md mb-4">
          <h3 className="font-heading text-base font-semibold text-foreground mb-3 flex items-center space-x-2">
            <Icon name="Scale" size={18} className="text-secondary" strokeWidth={2} aria-hidden="true" />
            <span>Dispute Resolution Timeframes</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Resolution Timeframe (days)"
              type="number"
              value={rules?.disputeResolutionTimeframe}
              onChange={(e) => handleInputChange('disputeResolutionTimeframe', e?.target?.value)}
              description="Days to resolve disputes"
              required
              aria-required="true"
            />
            <div className="space-y-3">
              <Checkbox
                label="Auto-escalate Unresolved Disputes"
                description="Automatically escalate after timeframe expires"
                checked={rules?.autoApproveBookings}
                onChange={(e) => handleInputChange('autoApproveBookings', e?.target?.checked)}
                aria-label="Auto-escalate unresolved disputes"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Booking Settings */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-6">
        <div className="p-4 bg-success/5 border border-success/20 rounded-md">
          <h3 className="font-heading text-base font-semibold text-foreground mb-3 flex items-center space-x-2">
            <Icon name="CheckCircle" size={18} className="text-success" strokeWidth={2} aria-hidden="true" />
            <span>Booking Configuration</span>
          </h3>
          <div className="space-y-3">
            <Checkbox
              label="Allow Instant Booking"
              description="Enable renters to book without owner approval"
              checked={rules?.allowInstantBooking}
              onChange={(e) => handleInputChange('allowInstantBooking', e?.target?.checked)}
              aria-label="Allow instant booking"
            />
            <Checkbox
              label="Require Insurance Verification"
              description="Mandate insurance documents before booking"
              checked={rules?.requireInsurance}
              onChange={(e) => handleInputChange('requireInsurance', e?.target?.checked)}
              aria-label="Require insurance verification"
            />
          </div>
        </div>
      </div>

      {/* Audit Trail */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <Icon name="Clock" size={16} aria-hidden="true" />
            <span>Last updated: {data?.updatedAt ? new Date(data?.updatedAt)?.toLocaleString() : 'Never'}</span>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            iconName="History"
            onClick={async () => {
              const trail = await platformConfigService?.getAuditTrail('platform_rules', 10);
              console.log('Audit trail:', trail);
            }}
            aria-label="View change history"
          >
            View Change History
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PlatformRulesConfig;